import React from 'react'
import { Paper, Box, Typography } from '@mui/material'
import MiniChart from './MiniChart'
import { SUMMARY_PALETTE, toneColor, deltaColor } from './summary.palette'

// MF-526 — Reusable KPI card: a big current value + a "good/bad"-coloured delta vs the previous
// period + a per-term mini-chart. Fully data-driven by one `stat` object, so the same component
// powers any metric here and, later, the bento chart bank.
//
// stat: {
//   label, value, unit, period, tone: 'good'|'bad',
//   delta: { value, unit, dir: 'up'|'down', good: boolean, text } | { flat: true } | null,
//   chart: 'line'|'bar', yMax?, trend: [{ t, v: number|null }]
// }
const SummaryStatCard = ({ stat }) => {
  if (!stat) return null
  const accent = toneColor(stat.tone)

  return (
    <Paper
      variant='outlined'
      sx={{
        borderColor: SUMMARY_PALETTE.cardBorder,
        borderRadius: '14px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        // MF-558 — taller card (as in the approved design) so the KPI block and the chart breathe.
        minHeight: 440
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: SUMMARY_PALETTE.muted }}>
        {stat.label}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 2 }}>
        <Typography
          sx={{ fontSize: 60, fontWeight: 800, lineHeight: 1, letterSpacing: '-.02em', color: accent }}
        >
          {stat.value}
          {stat.unit ? (
            <Box component='span' sx={{ fontSize: 32, fontWeight: 800 }}>
              {stat.unit}
            </Box>
          ) : null}
        </Typography>
        {stat.period ? (
          <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.faint }}>{stat.period}</Typography>
        ) : null}
      </Box>

      {stat.delta ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1.5,
            fontSize: 14,
            color: SUMMARY_PALETTE.muted
          }}
        >
          {stat.delta.flat ? (
            // No change vs the previous term — neutral, never a coloured up/down cue.
            <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Box component='span' sx={{ fontWeight: 700 }}>
                –
              </Box>
              No change vs past Term
            </Box>
          ) : (
            <>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: 22,
                  fontWeight: 700,
                  color: deltaColor(stat.delta.good)
                }}
              >
                <Box component='span' sx={{ fontSize: 16, lineHeight: 1 }}>
                  {stat.delta.dir === 'up' ? '▲' : '▼'}
                </Box>
                {stat.delta.value}
                {stat.delta.unit || ''}
              </Box>
              <Box component='span'>{stat.delta.text}</Box>
            </>
          )}
        </Box>
      ) : null}

      <Box sx={{ mt: 'auto', pt: 3 }}>
        <MiniChart type={stat.chart} trend={stat.trend} yMax={stat.yMax} color={accent} unit={stat.unit} />
      </Box>
    </Paper>
  )
}

export default SummaryStatCard
